/**
 * chatPrefsStore — vibe-designer 1.82.0 (fontSize) + 1.87.0 (thinkingLevel)
 *                + 1.97.0 (queueMode) + 1.99.1 (dismissedWarningTabs)
 *
 * Per-tenant Pi chat preferences:
 *   - fontSize (12-20px, default 14, since 1.82.0)
 *   - thinkingLevel ("off" | "low" | "medium" | "high", default "off",
 *     since 1.87.0)
 *   - queueMode ("followUp" | "steer", default "followUp", since 1.97.0)
 *   - dismissedWarningTabs (Set<tabId> of MP3.a context-overflow WARNING
 *     banner dismissals; survives page reload, since 1.99.1)
 *
 * Designed to grow with future Pi-chat-specific prefs (e.g. compact mode,
 * code-block theme) without bloating chatHarnessStore — that store is
 * specifically about WHICH harness mounts (legacy/pi/none), not how the
 * mounted harness looks or how it reasons.
 *
 * Storage keys:
 *   - vibe_pi_chat_font_size_${tenantId}             (1.82.0)
 *   - vibe_pi_chat_thinking_level_${tenantId}        (1.87.0)
 *   - vibe_pi_chat_queue_mode_${tenantId}            (1.97.0)
 *   - vibe_pi_chat_dismissed_warning_tabs_${tenantId} (1.99.1)
 *
 * Mirrors chatHarnessStore.ts isolation pattern. localStorage failures
 * (private mode, quota) silently fall back to defaults.
 *
 * thinkingLevel maps to pi-mono's ThinkingLevel enum
 * ("off" | "minimal" | "low" | "medium" | "high" | "xhigh"). We expose only
 * the four most useful for our local Qwen3.6-NVFP4 model: off / low /
 * medium / high. "minimal" and "xhigh" are skipped at the UI for clarity;
 * the agent state still accepts the full enum so a future runbook upgrade
 * can extend without store breakage.
 */
import { create } from 'zustand';

// 1.552.0 — ranges widened (were 12-20). Operators asked for the full
// spread of usable sizes; 10-28 covers dense laptops through large / low-vision
// displays. The input-area size (below) shares this clamp band.
const MIN = 10;
const MAX = 28;
const DEFAULT = 14;
const FONT_SIZE_STORAGE_KEY_PREFIX = 'vibe_pi_chat_font_size_';
// 1.552.0 — dedicated font size for the composer input area + status spinner,
// independent of the chat-transcript size above. The composer textarea already
// reads `--vibe-pi-input-font-size` (ChatPanelPi.tsx) with a fallback to the
// chat size; before 1.552.0 nothing set it, so the input never had its own knob.
const INPUT_FONT_SIZE_STORAGE_KEY_PREFIX = 'vibe_pi_chat_input_font_size_';
const THINKING_STORAGE_KEY_PREFIX = 'vibe_pi_chat_thinking_level_';
const QUEUE_MODE_STORAGE_KEY_PREFIX = 'vibe_pi_chat_queue_mode_';
const DISMISSED_WARNING_TABS_STORAGE_KEY_PREFIX = 'vibe_pi_chat_dismissed_warning_tabs_';

export const PI_CHAT_FONT_SIZE_RANGE = {
  min: MIN,
  max: MAX,
  default: DEFAULT,
} as const;

/** 1.552.0 — composer input-area + status-spinner font size. Same clamp band
 * as the transcript size; default matches so the input tracks the chat until
 * the operator sets it deliberately. */
export const PI_CHAT_INPUT_FONT_SIZE_RANGE = {
  min: MIN,
  max: MAX,
  default: DEFAULT,
} as const;

// 1.554.0 — Pi status-spinner theme prefs. Three per-tenant knobs:
//   spinnerVariant  — which animated theme the streaming indicator renders
//                     (one of SPINNER_VARIANTS, or 'rotate' to cycle them).
//   spinnerMessages — which theme's phrase pool the indicator rotates
//                     (same value set; 'rotate' unions all pools).
//   spinnerScale    — size multiplier for the animation + message (1×–3×),
//                     where 1× is the ORIGINAL spinner size and 2×/3× scale up.
// Default variant/messages/scale = 'cosmos' / 'cosmos' / 1× → the original
// orbiting-dot theme, its full original phrase pool, at its original size, so
// nothing changes for a user who never opens the picker: switching theme or
// growing the spinner is entirely opt-in.
//
// The concrete theme registry (Components + phrase pools) lives in
// components/layout/spinners/spinnerThemes.tsx; these ids are the shared
// contract between that file and this store (a spinnerThemes test asserts the
// registry ids match SPINNER_VARIANTS so the two can never drift).
export const SPINNER_VARIANTS = [
  'cosmos',
  'enchantment',
  'daynight',
  'puppy',
  'rainy',
] as const;
export type SpinnerVariantId = (typeof SPINNER_VARIANTS)[number];
/** Variant + messages selects accept the 5 themes plus the meta-value
 * 'rotate' (cycle themes / union all message pools). */
export const SPINNER_SELECTABLE = [...SPINNER_VARIANTS, 'rotate'] as const;
export type SpinnerSelectable = (typeof SPINNER_SELECTABLE)[number];
const DEFAULT_SPINNER_VARIANT: SpinnerSelectable = 'cosmos';
const DEFAULT_SPINNER_MESSAGES: SpinnerSelectable = 'cosmos';
/** Size multiplier for the spinner animation + its status message. 1× is the
 * ORIGINAL spinner size; 2×/3× scale it up relatively. Snaps to 0.25 steps;
 * scales the message-area font too (StreamingIndicator multiplies
 * `--vibe-pi-input-font-size` by this). */
export const SPINNER_SCALE_RANGE = { min: 1, max: 3, default: 1 } as const;
const SPINNER_VARIANT_STORAGE_KEY_PREFIX = 'vibe_pi_chat_spinner_variant_';
const SPINNER_MESSAGES_STORAGE_KEY_PREFIX = 'vibe_pi_chat_spinner_messages_';
const SPINNER_SCALE_STORAGE_KEY_PREFIX = 'vibe_pi_chat_spinner_scale_';

// 1.555.0 — spinner MESSAGE font (the "thinking" phrase). Default 'default'
// keeps the current Inter/system look, so the spinner is visually unchanged
// until a font is chosen. The family stacks + @font-face live in
// components/layout/spinners/spinnerFonts.ts; a test asserts that registry's
// ids equal SPINNER_FONT_VALUES so the two can't drift.
export const SPINNER_FONT_VALUES = [
  'default',
  'matrix',
  'kanji',
  'inter',
  'mono',
  'lora',
  'space',
  'playfair',
] as const;
export type SpinnerFontId = (typeof SPINNER_FONT_VALUES)[number];
const DEFAULT_SPINNER_FONT: SpinnerFontId = 'default';
const SPINNER_FONT_STORAGE_KEY_PREFIX = 'vibe_pi_chat_spinner_font_';

// 1.569.0 — in-thread ASCII "thought" spinner: WHICH Braille animation plays
// where Pi's next message appears. Distinct from the bottom StreamingIndicator
// theme (spinnerVariant) above. The ids MUST match ASCII_THOUGHT_MOVIES in
// components/layout/spinners/asciiThoughtFrames.ts (a store test asserts no
// drift). Rebuilt 2026-07-29: six native-small bright movies; default 'comet'.
// 'rotate' cycles them in order per thinking turn; 'random' picks a fresh one
// at random each turn.
export const ASCII_SPINNER_VARIANTS = ['comet', 'wave', 'orbit', 'pulse', 'runner', 'stream'] as const;
export type AsciiSpinnerVariantId = (typeof ASCII_SPINNER_VARIANTS)[number];
export const ASCII_SPINNER_SELECTABLE = [...ASCII_SPINNER_VARIANTS, 'rotate', 'random'] as const;
export type AsciiSpinnerSelectable = (typeof ASCII_SPINNER_SELECTABLE)[number];
const DEFAULT_ASCII_SPINNER: AsciiSpinnerSelectable = 'comet';
const ASCII_SPINNER_STORAGE_KEY_PREFIX = 'vibe_pi_chat_ascii_spinner_';

/** Subset of pi-mono's ThinkingLevel exposed in vibe's UI. The agent state
 * accepts the full enum so future levels can ship without a store break. */
export type ThinkingLevel = 'off' | 'low' | 'medium' | 'high';

const VALID_THINKING_LEVELS: readonly ThinkingLevel[] = ['off', 'low', 'medium', 'high'];

const DEFAULT_THINKING_LEVEL: ThinkingLevel = 'off';

/** Pi chat queue mode (1.97.0). Controls where a message goes when the user
 * submits in the queue input row while `state.isStreaming` is true.
 * - "followUp": queued message becomes the literal next user turn after the
 *   agent's current task finishes. Matches Claude Code mental model.
 * - "steer": queued message injects BETWEEN assistant turns inside the
 *   current run, modifying the agent's trajectory mid-task. */
export type QueueMode = 'followUp' | 'steer';

const VALID_QUEUE_MODES: readonly QueueMode[] = ['followUp', 'steer'];

const DEFAULT_QUEUE_MODE: QueueMode = 'followUp';

// 1.106.0: display-thinking toggle controls whether pi-mono's ThinkingBlock
// auto-expand effect fires for new <think> blocks. Independent of
// thinkingLevel — user can request reasoning but choose not to see it.
const DISPLAY_THINKING_STORAGE_KEY_PREFIX = 'vibe_pi_chat_display_thinking_';
const DEFAULT_DISPLAY_THINKING = false;

// 1.419.0 — auto-compact: when ON, crossing CONTEXT_WARNING_THRESHOLD
// triggers Pi chat compaction automatically (ChatPanelPi auto-compact
// effect). OFF by default — compaction rewrites conversation state into a
// summary; opting in is the user's call.
const AUTO_COMPACT_STORAGE_KEY_PREFIX = 'vibe_pi_chat_auto_compact_';
const DEFAULT_AUTO_COMPACT = false;

// 1.106.0: last non-off thinking level for OFF→ON toggle restoration.
// Updated whenever setThinkingLevel is called with a non-off value.
// 'off' is NEVER persisted into this field — semantics is "last NON-off".
const LAST_NON_OFF_THINKING_LEVEL_STORAGE_KEY_PREFIX = 'vibe_pi_chat_last_non_off_thinking_level_';
const DEFAULT_LAST_NON_OFF_THINKING_LEVEL: ThinkingLevel = 'medium';

// 1.108.0 Phase 3a — knowledge-services SOURCE toggles + cognitionSearchType.
// Per-tenant booleans gating which platform MCPs (mind/cortex/brain/sight/
// cognition/obsidian) participate in the chat-proxy.py RAG prefetch.
//
// 1.122.0 — defaults flipped to ALL-OFF per user directive 2026-05-13.
// Original 1.108.0 mind-chat-plugin-mirrored defaults (mind/cortex/cognition/
// obsidian = true, brain/sight = false) made every first-login chat ship 4
// MCP-source context blocks of 1-5K tokens each AND extended TTFT by 4
// parallel network round-trips, before the user had any chance to consent
// to or evaluate those sources. ALL-OFF is the principle-of-least-context
// default: fast first turn, no unsolicited knowledge injection, user
// opt-in per source. Per-tenant localStorage persistence means returning
// users keep whatever they last toggled.
const USE_MIND_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_mind_';
const USE_CORTEX_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_cortex_';
const USE_BRAIN_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_brain_';
const USE_SIGHT_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_sight_';
const USE_COGNITION_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_cognition_';
const USE_OBSIDIAN_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_obsidian_';
// 1.187.0 Phase 7.3 — per-tenant useWebSearch toggle. SOURCE_ICONS 7th entry.
const USE_WEB_SEARCH_STORAGE_KEY_PREFIX = 'vibe_pi_chat_use_web_search_';
const COGNITION_SEARCH_TYPE_STORAGE_KEY_PREFIX = 'vibe_pi_chat_cognition_search_type_';

const DEFAULT_USE_MIND = false;
const DEFAULT_USE_CORTEX = false;
const DEFAULT_USE_BRAIN = false;
const DEFAULT_USE_SIGHT = false;
const DEFAULT_USE_COGNITION = false;
const DEFAULT_USE_OBSIDIAN = false;
const DEFAULT_USE_WEB_SEARCH = false;
const DEFAULT_COGNITION_SEARCH_TYPE = 'GRAPH_COMPLETION';

function fontSizeStorageKeyFor(tenantId: string): string {
  return `${FONT_SIZE_STORAGE_KEY_PREFIX}${tenantId}`;
}

function inputFontSizeStorageKeyFor(tenantId: string): string {
  return `${INPUT_FONT_SIZE_STORAGE_KEY_PREFIX}${tenantId}`;
}

function thinkingStorageKeyFor(tenantId: string): string {
  return `${THINKING_STORAGE_KEY_PREFIX}${tenantId}`;
}

function queueModeStorageKeyFor(tenantId: string): string {
  return `${QUEUE_MODE_STORAGE_KEY_PREFIX}${tenantId}`;
}

function dismissedWarningTabsStorageKeyFor(tenantId: string): string {
  return `${DISMISSED_WARNING_TABS_STORAGE_KEY_PREFIX}${tenantId}`;
}

function clamp(n: number): number {
  return Math.max(MIN, Math.min(MAX, Math.round(n)));
}

function readStoredFontSize(tenantId: string | null): number {
  if (!tenantId) return DEFAULT;
  try {
    const raw = localStorage.getItem(fontSizeStorageKeyFor(tenantId));
    if (!raw) return DEFAULT;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= MIN && n <= MAX) return n;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT;
}

function writeStoredFontSize(tenantId: string, value: number): void {
  try {
    localStorage.setItem(fontSizeStorageKeyFor(tenantId), String(value));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function readStoredInputFontSize(tenantId: string | null): number {
  if (!tenantId) return DEFAULT;
  try {
    const raw = localStorage.getItem(inputFontSizeStorageKeyFor(tenantId));
    if (!raw) return DEFAULT;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= MIN && n <= MAX) return n;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT;
}

function writeStoredInputFontSize(tenantId: string, value: number): void {
  try {
    localStorage.setItem(inputFontSizeStorageKeyFor(tenantId), String(value));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

// 1.554.0 — spinner theme pref helpers.
function isSpinnerSelectable(raw: string): raw is SpinnerSelectable {
  return (SPINNER_SELECTABLE as readonly string[]).includes(raw);
}

// 1.569.0 — in-thread ASCII spinner pref helpers.
function isAsciiSpinnerSelectable(raw: string): raw is AsciiSpinnerSelectable {
  return (ASCII_SPINNER_SELECTABLE as readonly string[]).includes(raw);
}

function readStoredAsciiSpinner(tenantId: string | null): AsciiSpinnerSelectable {
  if (!tenantId) return DEFAULT_ASCII_SPINNER;
  try {
    const raw = localStorage.getItem(`${ASCII_SPINNER_STORAGE_KEY_PREFIX}${tenantId}`);
    if (raw && isAsciiSpinnerSelectable(raw)) return raw;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_ASCII_SPINNER;
}

function writeStoredAsciiSpinner(tenantId: string, value: AsciiSpinnerSelectable): void {
  try {
    localStorage.setItem(`${ASCII_SPINNER_STORAGE_KEY_PREFIX}${tenantId}`, value);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

/** Snap an arbitrary number to a valid spinner scale (0.25 steps, 1–3). */
function clampSpinnerScale(n: number): number {
  if (!Number.isFinite(n)) return SPINNER_SCALE_RANGE.default;
  const snapped = Math.round(n * 4) / 4;
  return Math.max(SPINNER_SCALE_RANGE.min, Math.min(SPINNER_SCALE_RANGE.max, snapped));
}

function readStoredSpinnerSelectable(
  prefix: string,
  tenantId: string | null,
  fallback: SpinnerSelectable,
): SpinnerSelectable {
  if (!tenantId) return fallback;
  try {
    const raw = localStorage.getItem(`${prefix}${tenantId}`);
    if (raw && isSpinnerSelectable(raw)) return raw;
  } catch {
    // localStorage unavailable — fall back.
  }
  return fallback;
}

function writeStoredSpinnerSelectable(
  prefix: string,
  tenantId: string,
  value: SpinnerSelectable,
): void {
  try {
    localStorage.setItem(`${prefix}${tenantId}`, value);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function readStoredSpinnerScale(tenantId: string | null): number {
  if (!tenantId) return SPINNER_SCALE_RANGE.default;
  try {
    const raw = localStorage.getItem(`${SPINNER_SCALE_STORAGE_KEY_PREFIX}${tenantId}`);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n)) return clampSpinnerScale(n);
    }
  } catch {
    // localStorage unavailable — fall back.
  }
  return SPINNER_SCALE_RANGE.default;
}

function writeStoredSpinnerScale(tenantId: string, value: number): void {
  try {
    localStorage.setItem(`${SPINNER_SCALE_STORAGE_KEY_PREFIX}${tenantId}`, String(value));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function isSpinnerFont(raw: string): raw is SpinnerFontId {
  return (SPINNER_FONT_VALUES as readonly string[]).includes(raw);
}

function readStoredSpinnerFont(tenantId: string | null): SpinnerFontId {
  if (!tenantId) return DEFAULT_SPINNER_FONT;
  try {
    const raw = localStorage.getItem(`${SPINNER_FONT_STORAGE_KEY_PREFIX}${tenantId}`);
    if (raw && isSpinnerFont(raw)) return raw;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_SPINNER_FONT;
}

function writeStoredSpinnerFont(tenantId: string, value: SpinnerFontId): void {
  try {
    localStorage.setItem(`${SPINNER_FONT_STORAGE_KEY_PREFIX}${tenantId}`, value);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function readStoredThinkingLevel(tenantId: string | null): ThinkingLevel {
  if (!tenantId) return DEFAULT_THINKING_LEVEL;
  try {
    const raw = localStorage.getItem(thinkingStorageKeyFor(tenantId));
    if (raw && (VALID_THINKING_LEVELS as readonly string[]).includes(raw)) {
      return raw as ThinkingLevel;
    }
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_THINKING_LEVEL;
}

function writeStoredThinkingLevel(tenantId: string, level: ThinkingLevel): void {
  try {
    localStorage.setItem(thinkingStorageKeyFor(tenantId), level);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function readStoredQueueMode(tenantId: string | null): QueueMode {
  if (!tenantId) return DEFAULT_QUEUE_MODE;
  try {
    const raw = localStorage.getItem(queueModeStorageKeyFor(tenantId));
    if (raw && (VALID_QUEUE_MODES as readonly string[]).includes(raw)) {
      return raw as QueueMode;
    }
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_QUEUE_MODE;
}

function writeStoredQueueMode(tenantId: string, mode: QueueMode): void {
  try {
    localStorage.setItem(queueModeStorageKeyFor(tenantId), mode);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

function readStoredDismissedWarningTabs(tenantId: string | null): Set<string> {
  if (!tenantId) return new Set();
  try {
    const raw = localStorage.getItem(dismissedWarningTabsStorageKeyFor(tenantId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    }
    return new Set();
  } catch {
    // localStorage unavailable OR malformed JSON — fall back to empty.
    return new Set();
  }
}

function writeStoredDismissedWarningTabs(tenantId: string, tabs: Set<string>): void {
  try {
    localStorage.setItem(
      dismissedWarningTabsStorageKeyFor(tenantId),
      JSON.stringify(Array.from(tabs)),
    );
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

// 1.106.0 — displayThinking helpers
function displayThinkingStorageKeyFor(tenantId: string): string {
  return `${DISPLAY_THINKING_STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStoredDisplayThinking(tenantId: string | null): boolean {
  if (!tenantId) return DEFAULT_DISPLAY_THINKING;
  try {
    const raw = localStorage.getItem(displayThinkingStorageKeyFor(tenantId));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_DISPLAY_THINKING;
}

function writeStoredDisplayThinking(tenantId: string, value: boolean): void {
  try {
    localStorage.setItem(displayThinkingStorageKeyFor(tenantId), String(value));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

// 1.419.0 — autoCompact helpers (mirrors displayThinking boolean pattern).
function autoCompactStorageKeyFor(tenantId: string): string {
  return `${AUTO_COMPACT_STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStoredAutoCompact(tenantId: string | null): boolean {
  if (!tenantId) return DEFAULT_AUTO_COMPACT;
  try {
    const raw = localStorage.getItem(autoCompactStorageKeyFor(tenantId));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_AUTO_COMPACT;
}

function writeStoredAutoCompact(tenantId: string, value: boolean): void {
  try {
    localStorage.setItem(autoCompactStorageKeyFor(tenantId), String(value));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

// 1.106.0 — lastNonOffThinkingLevel helpers
function lastNonOffThinkingLevelStorageKeyFor(tenantId: string): string {
  return `${LAST_NON_OFF_THINKING_LEVEL_STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStoredLastNonOffThinkingLevel(tenantId: string | null): ThinkingLevel {
  if (!tenantId) return DEFAULT_LAST_NON_OFF_THINKING_LEVEL;
  try {
    const raw = localStorage.getItem(lastNonOffThinkingLevelStorageKeyFor(tenantId));
    if (raw && raw !== 'off' && (VALID_THINKING_LEVELS as readonly string[]).includes(raw)) {
      return raw as ThinkingLevel;
    }
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_LAST_NON_OFF_THINKING_LEVEL;
}

function writeStoredLastNonOffThinkingLevel(tenantId: string, level: ThinkingLevel): void {
  if (level === 'off') return; // never persist 'off' here — semantics is "last NON-off"
  try {
    localStorage.setItem(lastNonOffThinkingLevelStorageKeyFor(tenantId), level);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

// 1.108.0 Phase 3a — SOURCE toggle helpers.
// One read/write pair per toggle; corrupted/missing values fall back to the
// mind-chat-VERIFIED default rather than coercing to falsy. cognitionSearchType
// accepts any non-empty string (mind-chat's enum is open — we don't gate the UI
// on it; backend handles validation).
function makeBooleanReader(prefix: string, defaultValue: boolean) {
  return (tenantId: string | null): boolean => {
    if (!tenantId) return defaultValue;
    try {
      const raw = localStorage.getItem(`${prefix}${tenantId}`);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // localStorage unavailable — fall back.
    }
    return defaultValue;
  };
}

function makeBooleanWriter(prefix: string) {
  return (tenantId: string, value: boolean): void => {
    try {
      localStorage.setItem(`${prefix}${tenantId}`, String(value));
    } catch {
      // Quota / disabled storage — runtime stays correct.
    }
  };
}

const readStoredUseMind = makeBooleanReader(USE_MIND_STORAGE_KEY_PREFIX, DEFAULT_USE_MIND);
const writeStoredUseMind = makeBooleanWriter(USE_MIND_STORAGE_KEY_PREFIX);
const readStoredUseCortex = makeBooleanReader(USE_CORTEX_STORAGE_KEY_PREFIX, DEFAULT_USE_CORTEX);
const writeStoredUseCortex = makeBooleanWriter(USE_CORTEX_STORAGE_KEY_PREFIX);
const readStoredUseBrain = makeBooleanReader(USE_BRAIN_STORAGE_KEY_PREFIX, DEFAULT_USE_BRAIN);
const writeStoredUseBrain = makeBooleanWriter(USE_BRAIN_STORAGE_KEY_PREFIX);
const readStoredUseSight = makeBooleanReader(USE_SIGHT_STORAGE_KEY_PREFIX, DEFAULT_USE_SIGHT);
const writeStoredUseSight = makeBooleanWriter(USE_SIGHT_STORAGE_KEY_PREFIX);
const readStoredUseCognition = makeBooleanReader(USE_COGNITION_STORAGE_KEY_PREFIX, DEFAULT_USE_COGNITION);
const writeStoredUseCognition = makeBooleanWriter(USE_COGNITION_STORAGE_KEY_PREFIX);
const readStoredUseObsidian = makeBooleanReader(USE_OBSIDIAN_STORAGE_KEY_PREFIX, DEFAULT_USE_OBSIDIAN);
const writeStoredUseObsidian = makeBooleanWriter(USE_OBSIDIAN_STORAGE_KEY_PREFIX);
const readStoredUseWebSearch = makeBooleanReader(USE_WEB_SEARCH_STORAGE_KEY_PREFIX, DEFAULT_USE_WEB_SEARCH);
const writeStoredUseWebSearch = makeBooleanWriter(USE_WEB_SEARCH_STORAGE_KEY_PREFIX);

function readStoredCognitionSearchType(tenantId: string | null): string {
  if (!tenantId) return DEFAULT_COGNITION_SEARCH_TYPE;
  try {
    const raw = localStorage.getItem(`${COGNITION_SEARCH_TYPE_STORAGE_KEY_PREFIX}${tenantId}`);
    if (raw && raw.length > 0) return raw;
  } catch {
    // localStorage unavailable — fall back.
  }
  return DEFAULT_COGNITION_SEARCH_TYPE;
}

function writeStoredCognitionSearchType(tenantId: string, value: string): void {
  try {
    localStorage.setItem(`${COGNITION_SEARCH_TYPE_STORAGE_KEY_PREFIX}${tenantId}`, value);
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

interface ChatPrefsState {
  fontSize: number;
  /** 1.552.0 — composer input-area + status-spinner font size (per-tenant).
   * Drives `--vibe-pi-input-font-size`; falls back to `fontSize` when unset. */
  inputFontSize: number;
  /** 1.554.0 — which animated theme the Pi streaming indicator renders. One of
   * SPINNER_VARIANTS, or 'rotate' to cycle them. Default 'cosmos'. */
  spinnerVariant: SpinnerSelectable;
  /** 1.554.0 — which theme's phrase pool the indicator rotates. Same value set;
   * 'rotate' unions all pools. Default 'cosmos'. */
  spinnerMessages: SpinnerSelectable;
  /** 1.554.0 — size multiplier for the spinner animation + message (1–3). */
  spinnerScale: number;
  /** 1.555.0 — font for the spinner MESSAGE (one of SPINNER_FONT_VALUES).
   * Default 'default' = the current Inter/system look. */
  spinnerFont: SpinnerFontId;
  /** 1.569.0 — which in-thread ASCII "thought" animation plays (one of
   * ASCII_SPINNER_VARIANTS), or 'rotate' to cycle them per turn. Default
   * 'rotate'. */
  asciiSpinner: AsciiSpinnerSelectable;
  thinkingLevel: ThinkingLevel;
  queueMode: QueueMode;
  /** Set of tab IDs whose MP3.a WARNING context-overflow banner the user
   * has dismissed. Per-tenant; persists across page reloads. Once a tab
   * is in this set, the WARNING banner stays hidden for that tab's
   * lifetime. The ERROR banner (post-overflow) is independent and still
   * fires regardless of dismissal state. (1.99.1) */
  dismissedWarningTabs: Set<string>;
  /** 1.106.0: chrome-toggle controls whether pi-mono's ThinkingBlock
   * auto-expand effect fires for new <think> blocks. Independent of
   * thinkingLevel — user can request reasoning but choose not to see it. */
  displayThinking: boolean;
  /** 1.419.0 — auto-run Pi chat compaction when the WARNING threshold is crossed. */
  autoCompact: boolean;
  /** 1.106.0: last non-off thinking level for OFF→ON chrome-toggle
   * restoration. Default 'medium' on fresh tenant. Updated by
   * setThinkingLevel whenever a non-off value is set. */
  lastNonOffThinkingLevel: ThinkingLevel;
  /** Hydrate all prefs from localStorage given the tenant id. */
  hydrate: (tenantId: string | null) => void;
  /** Update the font size + persist for the given tenant. Clamps to the range. */
  setFontSize: (tenantId: string, size: number) => void;
  /** 1.552.0 — update the composer input-area font size + persist. Clamps to range. */
  setInputFontSize: (tenantId: string, size: number) => void;
  /** 1.554.0 — set the spinner theme + persist. Invalid values coerce to 'cosmos'. */
  setSpinnerVariant: (tenantId: string, variant: SpinnerSelectable) => void;
  /** 1.554.0 — set the spinner message pool + persist. Invalid coerce to 'cosmos'. */
  setSpinnerMessages: (tenantId: string, messages: SpinnerSelectable) => void;
  /** 1.554.0 — set the spinner size multiplier + persist. Snaps to 0.25 steps, 1–3. */
  setSpinnerScale: (tenantId: string, scale: number) => void;
  /** 1.555.0 — set the spinner message font + persist. Invalid coerce to 'default'. */
  setSpinnerFont: (tenantId: string, font: SpinnerFontId) => void;
  /** 1.569.0 — set the in-thread ASCII animation + persist. Invalid coerce to 'rotate'. */
  setAsciiSpinner: (tenantId: string, variant: AsciiSpinnerSelectable) => void;
  /** Update the thinking level + persist for the given tenant. Invalid values
   * are coerced to "off". (1.106.0) Any non-off value also updates
   * lastNonOffThinkingLevel; 'off' preserves the prior value. */
  setThinkingLevel: (tenantId: string, level: ThinkingLevel) => void;
  /** Update the queue mode + persist for the given tenant. Invalid values
   * are coerced to "followUp". */
  setQueueMode: (tenantId: string, mode: QueueMode) => void;
  /** Add a tab id to the dismissed-WARNING-banner set. Idempotent — second
   * call for the same tabId is a no-op (1.99.1). */
  addDismissedWarningTab: (tenantId: string, tabId: string) => void;
  /** 1.106.0: flip the display-reasoning chrome toggle. */
  setDisplayThinking: (tenantId: string, value: boolean) => void;
  setAutoCompact: (tenantId: string, value: boolean) => void;
  /** 1.108.0: knowledge-services SOURCE toggles. Each gates whether the
   * corresponding platform MCP participates in the chat-proxy.py RAG prefetch.
   * Defaults from mind-chat chatStore.ts:76-83 (fast semantic-rich sources ON,
   * GPU-heavy or lower-quality sources OFF). Phase 3a wires only useMind into
   * the backend; other 5 render disabled-with-tooltip until Phase 3b+. */
  useMind: boolean;
  useCortex: boolean;
  useBrain: boolean;
  useSight: boolean;
  useCognition: boolean;
  useObsidian: boolean;
  /** 1.187.0 Phase 7.3: per-tenant gate on whether the voice/Pi-chat
   * dispatcher's LLM may consider the `web_search` AgentTool. When false,
   * backend conditionally omits the web_search system-prompt block AND
   * filters it from `_VOICE_TOOLS_OPENAI_SCHEMA` (native-tools API path). */
  useWebSearch: boolean;
  /** 1.108.0: cognee search_type passed to the cognition-mcp /api/v1/search
   * endpoint when useCognition is true. Default 'GRAPH_COMPLETION' per mind-chat
   * chatStore.ts:82. Free-form string — backend validates. */
  cognitionSearchType: string;
  setUseMind: (tenantId: string, value: boolean) => void;
  setUseCortex: (tenantId: string, value: boolean) => void;
  setUseBrain: (tenantId: string, value: boolean) => void;
  setUseSight: (tenantId: string, value: boolean) => void;
  setUseCognition: (tenantId: string, value: boolean) => void;
  setUseObsidian: (tenantId: string, value: boolean) => void;
  setUseWebSearch: (tenantId: string, value: boolean) => void;
  setCognitionSearchType: (tenantId: string, value: string) => void;
}

export const useChatPrefsStore = create<ChatPrefsState>((set) => ({
  fontSize: DEFAULT,
  inputFontSize: DEFAULT,
  spinnerVariant: DEFAULT_SPINNER_VARIANT,
  spinnerMessages: DEFAULT_SPINNER_MESSAGES,
  spinnerScale: SPINNER_SCALE_RANGE.default,
  spinnerFont: DEFAULT_SPINNER_FONT,
  asciiSpinner: DEFAULT_ASCII_SPINNER,
  thinkingLevel: DEFAULT_THINKING_LEVEL,
  queueMode: DEFAULT_QUEUE_MODE,
  dismissedWarningTabs: new Set<string>(),
  displayThinking: DEFAULT_DISPLAY_THINKING,
  autoCompact: DEFAULT_AUTO_COMPACT,
  lastNonOffThinkingLevel: DEFAULT_LAST_NON_OFF_THINKING_LEVEL,
  useMind: DEFAULT_USE_MIND,
  useCortex: DEFAULT_USE_CORTEX,
  useBrain: DEFAULT_USE_BRAIN,
  useSight: DEFAULT_USE_SIGHT,
  useCognition: DEFAULT_USE_COGNITION,
  useObsidian: DEFAULT_USE_OBSIDIAN,
  useWebSearch: DEFAULT_USE_WEB_SEARCH,
  cognitionSearchType: DEFAULT_COGNITION_SEARCH_TYPE,
  hydrate: (tenantId) =>
    set({
      fontSize: readStoredFontSize(tenantId),
      inputFontSize: readStoredInputFontSize(tenantId),
      spinnerVariant: readStoredSpinnerSelectable(
        SPINNER_VARIANT_STORAGE_KEY_PREFIX,
        tenantId,
        DEFAULT_SPINNER_VARIANT,
      ),
      spinnerMessages: readStoredSpinnerSelectable(
        SPINNER_MESSAGES_STORAGE_KEY_PREFIX,
        tenantId,
        DEFAULT_SPINNER_MESSAGES,
      ),
      spinnerScale: readStoredSpinnerScale(tenantId),
      spinnerFont: readStoredSpinnerFont(tenantId),
      asciiSpinner: readStoredAsciiSpinner(tenantId),
      thinkingLevel: readStoredThinkingLevel(tenantId),
      queueMode: readStoredQueueMode(tenantId),
      dismissedWarningTabs: readStoredDismissedWarningTabs(tenantId),
      displayThinking: readStoredDisplayThinking(tenantId),
      autoCompact: readStoredAutoCompact(tenantId),
      lastNonOffThinkingLevel: readStoredLastNonOffThinkingLevel(tenantId),
      useMind: readStoredUseMind(tenantId),
      useCortex: readStoredUseCortex(tenantId),
      useBrain: readStoredUseBrain(tenantId),
      useSight: readStoredUseSight(tenantId),
      useCognition: readStoredUseCognition(tenantId),
      useObsidian: readStoredUseObsidian(tenantId),
      useWebSearch: readStoredUseWebSearch(tenantId),
      cognitionSearchType: readStoredCognitionSearchType(tenantId),
    }),
  setFontSize: (tenantId, size) => {
    const clamped = clamp(size);
    writeStoredFontSize(tenantId, clamped);
    set({ fontSize: clamped });
  },
  setInputFontSize: (tenantId, size) => {
    const clamped = clamp(size);
    writeStoredInputFontSize(tenantId, clamped);
    set({ inputFontSize: clamped });
  },
  setSpinnerVariant: (tenantId, variant) => {
    const safe: SpinnerSelectable = isSpinnerSelectable(variant)
      ? variant
      : DEFAULT_SPINNER_VARIANT;
    writeStoredSpinnerSelectable(SPINNER_VARIANT_STORAGE_KEY_PREFIX, tenantId, safe);
    set({ spinnerVariant: safe });
  },
  setSpinnerMessages: (tenantId, messages) => {
    const safe: SpinnerSelectable = isSpinnerSelectable(messages)
      ? messages
      : DEFAULT_SPINNER_MESSAGES;
    writeStoredSpinnerSelectable(SPINNER_MESSAGES_STORAGE_KEY_PREFIX, tenantId, safe);
    set({ spinnerMessages: safe });
  },
  setSpinnerScale: (tenantId, scale) => {
    const snapped = clampSpinnerScale(scale);
    writeStoredSpinnerScale(tenantId, snapped);
    set({ spinnerScale: snapped });
  },
  setSpinnerFont: (tenantId, font) => {
    const safe: SpinnerFontId = isSpinnerFont(font) ? font : DEFAULT_SPINNER_FONT;
    writeStoredSpinnerFont(tenantId, safe);
    set({ spinnerFont: safe });
  },
  setAsciiSpinner: (tenantId, variant) => {
    const safe: AsciiSpinnerSelectable = isAsciiSpinnerSelectable(variant)
      ? variant
      : DEFAULT_ASCII_SPINNER;
    writeStoredAsciiSpinner(tenantId, safe);
    set({ asciiSpinner: safe });
  },
  setThinkingLevel: (tenantId, level) => {
    const safe: ThinkingLevel = (VALID_THINKING_LEVELS as readonly string[]).includes(level)
      ? level
      : DEFAULT_THINKING_LEVEL;
    writeStoredThinkingLevel(tenantId, safe);
    // 1.106.0: capture last non-off level for OFF→ON chrome-toggle
    // restoration. 'off' itself is NEVER persisted into
    // lastNonOffThinkingLevel — semantics is "last NON-off value".
    if (safe !== 'off') {
      writeStoredLastNonOffThinkingLevel(tenantId, safe);
      set({ thinkingLevel: safe, lastNonOffThinkingLevel: safe });
    } else {
      set({ thinkingLevel: safe });
    }
  },
  setQueueMode: (tenantId, mode) => {
    const safe: QueueMode = (VALID_QUEUE_MODES as readonly string[]).includes(mode)
      ? mode
      : DEFAULT_QUEUE_MODE;
    writeStoredQueueMode(tenantId, safe);
    set({ queueMode: safe });
  },
  addDismissedWarningTab: (tenantId, tabId) => {
    // G0 [B4]: read fresh state via getState() — avoids stale-closure from
    // React renders (NOT a concurrent-execution race; JS is single-threaded).
    // Zustand getState() returns the current store synchronously.
    const current = useChatPrefsStore.getState().dismissedWarningTabs;
    if (current.has(tabId)) return; // idempotent
    const next = new Set(current);
    next.add(tabId);
    writeStoredDismissedWarningTabs(tenantId, next);
    set({ dismissedWarningTabs: next });
  },
  setDisplayThinking: (tenantId, value) => {
    writeStoredDisplayThinking(tenantId, value);
    set({ displayThinking: value });
  },
  setAutoCompact: (tenantId, value) => {
    writeStoredAutoCompact(tenantId, value);
    set({ autoCompact: value });
  },
  setUseMind: (tenantId, value) => {
    writeStoredUseMind(tenantId, value);
    set({ useMind: value });
  },
  setUseCortex: (tenantId, value) => {
    writeStoredUseCortex(tenantId, value);
    set({ useCortex: value });
  },
  setUseBrain: (tenantId, value) => {
    writeStoredUseBrain(tenantId, value);
    set({ useBrain: value });
  },
  setUseSight: (tenantId, value) => {
    writeStoredUseSight(tenantId, value);
    set({ useSight: value });
  },
  setUseCognition: (tenantId, value) => {
    writeStoredUseCognition(tenantId, value);
    set({ useCognition: value });
  },
  setUseObsidian: (tenantId, value) => {
    writeStoredUseObsidian(tenantId, value);
    set({ useObsidian: value });
  },
  setUseWebSearch: (tenantId, value) => {
    writeStoredUseWebSearch(tenantId, value);
    set({ useWebSearch: value });
  },
  setCognitionSearchType: (tenantId, value) => {
    writeStoredCognitionSearchType(tenantId, value);
    set({ cognitionSearchType: value });
  },
}));
